// Термические фронты = линии максимального градиента температуры (осевые линии,
// гребень поля |∇T|). Считаются в БРАУЗЕРЕ по той же сетке SST, что и изотермы, —
// ползунок силы не ходит в сеть, только пересчитывает по кэшу.
//
// Фронт ≠ изотерма: вдоль фронта температура МЕНЯЕТСЯ, поэтому подписываем её в
// нескольких точках вдоль линии.
//
// Алгоритм: сглаживание → градиент (°C/км с широтной коррекцией) → гребень
// (non-max suppression вдоль градиента) → отрезки вдоль оси фронта + точки-подписи.

import type { SstGrid } from './marchingSquares'

const KM_PER_DEG_LAT = 110.574
const KM_PER_DEG_LON = 111.320 // на экваторе; далее умножаем на cos(lat)
const DEG2RAD = Math.PI / 180

type Pt = [number, number] // [lon, lat]

export interface FrontResult {
  line: GeoJSON.Feature<GeoJSON.MultiLineString, { strength: number }>
  labels: GeoJSON.FeatureCollection<GeoJSON.Point, { temp: number }>
}

/** Сглаживание 3×3 ТОЛЬКО там, где окно полное (все 9 соседей на месте).
 *
 *  Усредняем лишь при полной симметричной окрестности: симметричное среднее не
 *  трогает линейное поле (равномерный наклон остаётся равномерным → фальшивого
 *  фронта на краю/у дыры не возникает). У края и рядом с дырой оставляем сырое
 *  значение — обрезанное несимметричное окно как раз и рождало бы ложный гребень. */
function smooth(values: (number | null)[][], rows: number, cols: number): (number | null)[][] {
  const out: (number | null)[][] = []
  for (let i = 0; i < rows; i++) {
    const row: (number | null)[] = []
    for (let j = 0; j < cols; j++) {
      const c = values[i][j]
      if (c == null || i === 0 || j === 0 || i === rows - 1 || j === cols - 1) {
        row.push(c)
        continue
      }
      let sum = 0
      let full = true
      for (let di = -1; di <= 1 && full; di++) {
        for (let dj = -1; dj <= 1; dj++) {
          const v = values[i + di][j + dj]
          if (v == null) {
            full = false
            break
          }
          sum += v
        }
      }
      row.push(full ? sum / 9 : c)
    }
    out.push(row)
  }
  return out
}

interface Gradient {
  /** |∇T| в °C/км; null там, где не хватает соседей (край/дыра). */
  mag: (number | null)[][]
  /** Компоненты градиента в ИНДЕКСНОМ пространстве: °C на клетку.
   *  gj — на восток (j+1), gi — на север (i−1, т.е. знак «север положителен»). */
  gj: number[][]
  gi: number[][]
  /** Масштаб клетки в км по осям (для перевода отрезков обратно в градусы). */
  dxKm: number[]
  dyKm: number
}

function gradientField(grid: SstGrid, field: (number | null)[][]): Gradient {
  const { rows, cols, step, latTop } = grid
  // Оси порознь: на балтийской миле клетка 1/60° × 1/36°, и общий шаг занизил
  // бы |∇T| по долготе почти вдвое — гребень фронта уехал бы вместе с ним.
  const stepLat = grid.stepLat ?? step
  const stepLon = grid.stepLon ?? step
  const dyKm = stepLat * KM_PER_DEG_LAT
  const dxKm: number[] = []
  const mag: (number | null)[][] = []
  const gj: number[][] = []
  const gi: number[][] = []
  for (let i = 0; i < rows; i++) {
    const lat = latTop - i * stepLat
    dxKm[i] = stepLon * KM_PER_DEG_LON * Math.cos(lat * DEG2RAD)
    mag[i] = new Array(cols).fill(null)
    gj[i] = new Array(cols).fill(0)
    gi[i] = new Array(cols).fill(0)
  }
  for (let i = 1; i < rows - 1; i++) {
    for (let j = 1; j < cols - 1; j++) {
      const e = field[i][j + 1]
      const w = field[i][j - 1]
      const n = field[i - 1][j] // север
      const s = field[i + 1][j]
      if (e == null || w == null || n == null || s == null || field[i][j] == null) continue
      // °C на клетку (центральная разность / 2)
      const dTdj = (e - w) / 2 // восток
      const dTdi = (n - s) / 2 // север положителен
      gj[i][j] = dTdj
      gi[i][j] = dTdi
      // °C/км: делим на км-размер ДВУХ клеток (центральная разность через 2 шага)
      const gxKm = (e - w) / (2 * dxKm[i])
      const gyKm = (n - s) / (2 * dyKm)
      mag[i][j] = Math.hypot(gxKm, gyKm)
    }
  }
  return { mag, gj, gi, dxKm, dyKm }
}

/** Наибольший |∇T| по сетке в °C/100 км — для дефолта/подсказки ползунка. */
export function peakGradientPer100km(grid: SstGrid): number {
  const field = smooth(grid.values, grid.rows, grid.cols)
  const { mag } = gradientField(grid, field)
  let peak = 0
  for (const row of mag) for (const m of row) if (m != null && m > peak) peak = m
  return peak * 100
}

// Соседи по 8-окрестности в СЕВЕР-ВВЕРХ угловой раскладке: сектор k = k·45°,
// где 0°=восток, 45°=северо-восток и т.д. drow=−1 — это север (строка 0 сверху).
const _NEIGHBOURS: [number, number][] = [
  [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1],
]

/** Два соседа ПОПЕРЁК фронта (по направлению градиента) для non-max suppression.
 *  gi — компонента на СЕВЕР (>0), gj — на восток; угол берём как atan2(gi, gj),
 *  т.е. в «север-вверх» системе, ровно как раскладка `_NEIGHBOURS`. Ошибка знака
 *  строки тут сдвигала бы пару соседей и клала гребень мимо оси наклонного фронта. */
function acrossNeighbours(gi: number, gj: number): [[number, number], [number, number]] {
  const ang = Math.atan2(gi, gj)
  const sector = ((Math.round(ang / (Math.PI / 4)) % 8) + 8) % 8
  const a = _NEIGHBOURS[sector]
  return [a, [-a[0], -a[1]]]
}

export function detectFronts(grid: SstGrid, strengthPer100km: number): FrontResult {
  const { rows, cols, step, latTop, lonLeft } = grid
  const stepLat = grid.stepLat ?? step
  const stepLon = grid.stepLon ?? step
  const field = smooth(grid.values, rows, cols)
  const { mag, gi, gj, dxKm, dyKm } = gradientField(grid, field)
  const threshold = strengthPer100km / 100 // °C/км

  const segments: [Pt, Pt][] = []
  const labels: GeoJSON.Feature<GeoJSON.Point, { temp: number }>[] = []

  const lon = (j: number) => lonLeft + j * stepLon
  const lat = (i: number) => latTop - i * stepLat

  for (let i = 1; i < rows - 1; i++) {
    for (let j = 1; j < cols - 1; j++) {
      const m = mag[i][j]
      if (m == null || m < threshold) continue

      // Градиент в ФИЗИЧЕСКИХ км-осях (с широтной коррекцией). По нему выбираем и
      // соседей NMS, и ось: индексное направление на широте 60–66° повёрнуто
      // относительно физического до ~20° (клетка не квадратная в км), и пара
      // соседей могла бы съехать на N/S вместо NE/SW.
      const gxKm = gj[i][j] / dxKm[i] // восток, °C/км
      const gyKm = gi[i][j] / dyKm // север, °C/км
      const len = Math.hypot(gxKm, gyKm)
      if (len === 0) continue

      // NMS: |∇T| должен быть локальным максимумом ПОПЕРЁК фронта (вдоль градиента).
      const [[da, db], [ea, eb]] = acrossNeighbours(gyKm, gxKm)
      const mA = mag[i + da]?.[j + db] ?? null
      const mB = mag[i + ea]?.[j + eb] ?? null
      if (mA == null && mB == null) continue // не с чем сравнить — не гребень
      if (mA != null && m < mA) continue
      if (mB != null && m < mB) continue
      // Строгий максимум хотя бы с одной присутствующей стороны: иначе плато
      // равномерного наклона (|∇T| всюду равен) прошло бы как сплошной «гребень».
      const strictMax = (mA != null && m > mA) || (mB != null && m > mB)
      if (!strictMax) continue

      // Ось фронта — ВДОЛЬ, т.е. перпендикулярно градиенту: t = (−gyKm, gxKm).
      const txKm = -gyKm / len
      const tyKm = gxKm / len
      // Отрезок ~1.5 клетки, чтобы соседние сливались. Км-смещение вдоль оси
      // переводим обратно в градусы (по своей широте для долготы).
      const halfKm = 0.75 * dyKm
      const dLon = (txKm * halfKm) / (KM_PER_DEG_LON * Math.cos(lat(i) * DEG2RAD))
      const dLat = (tyKm * halfKm) / KM_PER_DEG_LAT
      const c: Pt = [lon(j), lat(i)]
      segments.push([
        [c[0] - dLon, c[1] - dLat],
        [c[0] + dLon, c[1] + dLat],
      ])

      // Подпись T в этой точке — целый градус. MapLibre сам отбросит налезающие.
      const t = field[i][j]
      if (t != null) {
        labels.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [c[0], c[1]] },
          properties: { temp: Math.round(t) },
        })
      }
    }
  }

  return {
    line: {
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: segments },
      properties: { strength: strengthPer100km },
    },
    labels: { type: 'FeatureCollection', features: labels },
  }
}
